import {
  Collapse,
  Heading,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { ScenarioModel } from "../../clients/KompasApi";
import { tempCalculatePrice as calculateFacadePrice } from "../../components/EnergyMeasureTable/FacadeMeasuresSubTable";
import { tempCalculatePrice as calculateFloorPrice } from "../../components/EnergyMeasureTable/FloorMeasuresSubTable";
import { tempCalculatePrice as calculateFramePrice } from "../../components/EnergyMeasureTable/FrameMeasuresSubTable";
import { tempCalculatePrice as calculateHeatingPrice } from "../../components/EnergyMeasureTable/HeatingMeasuresSubTable";
import { tempCalculatePrice as calculateRoofInsulationPrice } from "../../components/EnergyMeasureTable/RoofInsulationMeasuresSubTable";
import { tempCalculatePrice as calculateVentilationPrice } from "../../components/EnergyMeasureTable/VentilationMeasuresSubTable";
import PriceDisplay from "../../elements/PriceDisplay";
import { useAtom } from "jotai";
import {
  showPricesAtom,
  showTaxAtom,
} from "../modals/ConfigureCompareViewModal";
import { ExpandChevron } from "../../elements/ExpandChevron";
import { useCallback } from "react";
import { calculateTax, defaultTaxValue, TaxKey, taxMapAtom } from "../../components/TaxSelect";
import { useComparePageContext } from "../../shared/state/ComparePageContext";
import { DividerRow } from "../../components/EnergyMeasureTable/DividerRow";

export function CompareTotalPriceTable() {
  const { scenarios } = useComparePageContext();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const [showPrices] = useAtom(showPricesAtom);
  const [showTax] = useAtom(showTaxAtom);
  const [taxMap] = useAtom(taxMapAtom);

  const calculateBasePrice = useCallback(
    (scenario: ScenarioModel) =>
      calculateFacadePrice(scenario) +
      calculateFramePrice(scenario) +
      calculateFloorPrice(scenario) +
      calculateRoofInsulationPrice(scenario) +
      calculateVentilationPrice(scenario) +
      calculateHeatingPrice(scenario),
    [],
  );

  const calculateTotalTax = useCallback(
    (scenario: ScenarioModel) => {
      const taxValues = taxMap[scenario.id] ?? {};

      const facadeTax = calculateTax(
        calculateFacadePrice(scenario),
        taxValues[TaxKey.Facade] ?? defaultTaxValue,
      );
      const frameTax = calculateTax(
        calculateFramePrice(scenario),
        taxValues[TaxKey.Frame] ?? defaultTaxValue,
      );
      const floorTax = calculateTax(
        calculateFloorPrice(scenario),
        taxValues[TaxKey.Floor] ?? defaultTaxValue,
      );
      const roofInsulationTax = calculateTax(
        calculateRoofInsulationPrice(scenario),
        taxValues[TaxKey.Roof] ?? defaultTaxValue,
      );
      const ventilationTax = calculateTax(
        calculateVentilationPrice(scenario),
        taxValues[TaxKey.Ventilation] ?? defaultTaxValue,
      );
      const heatingTax = calculateTax(
        calculateHeatingPrice(scenario),
        taxValues[TaxKey.Heating] ?? defaultTaxValue,
      );

      return (
        facadeTax +
        frameTax +
        floorTax +
        roofInsulationTax +
        ventilationTax +
        heatingTax
      );
    },
    [taxMap],
  );

  const calculateTotalPrice = useCallback(
    (scenario: ScenarioModel) => {
      const basePrice = calculateBasePrice(scenario);
      const totalTax = calculateTotalTax(scenario);

      return basePrice + totalTax * +showTax;
    },
    [calculateBasePrice, calculateTotalTax, showTax],
  );

  if (!showPrices) return null;

  return (
    <>
      <Heading
        cursor="pointer"
        mb={isOpen ? 0 : 4}
        ml={6}
        onClick={onToggle}
        size="xs"
        w="max-content"
      >
        <ExpandChevron forHeading isOpen={isOpen} /> Kassabon
      </Heading>
      <Collapse in={isOpen}>
        <Table
          className={isOpen ? "open" : "closed"}
          sx={{ borderSpacing: "2rem 0" }}
          variant="compare"
        >
          <Thead>
            <Tr>
              <Th />
              <Th />
              {scenarios.map((scenario) => (
                <Th
                  key={scenario.id}
                  sx={{
                    bg: "brand.skyblue.120 !important",
                    color: "black !important",
                    pb: "4 !important",
                    textAlign: "start !important",
                  }}
                >
                  Ramingsprijs
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {showTax && (
              <>
                <Tr className="prevent-hover">
                  <Td />
                  <Td />
                  {scenarios.map((scenario) => (
                    <Td key={scenario.id} py="2 !important">
                      <HStack justifyContent="space-between">
                        <Text>Subtotaal</Text>
                        <PriceDisplay
                          fontWeight="bold"
                          price={calculateBasePrice(scenario)}
                        />
                      </HStack>
                    </Td>
                  ))}
                </Tr>
                <Tr className="prevent-hover">
                  <Td />
                  <Td />
                  {scenarios.map((scenario) => (
                    <Td key={scenario.id} py="2 !important">
                      <HStack justifyContent="space-between">
                        <Text>BTW</Text>
                        <PriceDisplay
                          fontWeight="bold"
                          price={calculateTotalTax(scenario)}
                        />
                      </HStack>
                    </Td>
                  ))}
                </Tr>
                <DividerRow />
              </>
            )}
            <Tr className="prevent-hover">
              <Td />
              <Td />
              {scenarios.map((scenario) => (
                <Td key={scenario.id} py="2 !important">
                  <HStack justifyContent="space-between">
                    <Text>Totaal</Text>
                    <PriceDisplay
                      fontWeight="bold"
                      price={calculateTotalPrice(scenario)}
                    />
                  </HStack>
                </Td>
              ))}
            </Tr>
          </Tbody>
        </Table>
      </Collapse>
    </>
  );
}
